// src/pages/LoginPage.tsx
import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { jwtDecode } from 'jwt-decode'
import { useTranslation } from 'react-i18next'
import ParticlesComponent from '../../components/particles/Particles'
import LoginForm from '../../components/Login/LoginForm'
import RegisterForm from '../../components/Login/RegisterForm'
import ForgotPasswordForm from '../../components/Login/ForgotPasswordForm'
import ConfirmationEmailSent from '../../components/Login/ConfirmationEmailSent'
import ErrorModal from '../../components/Login/ErrorModal'
import Notification from '../../components/misc/Notification'
import { loginUser } from '../../api/userService'

interface DecodedToken {
  user_id: number
  email: string
  is_admin: boolean
  language_code: string
  exp: number
}

const LoginPage: React.FC = () => {
  const [isRegister, setIsRegister] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [showForgot, setShowForgot] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [notify, setNotify] = useState(false)
  const [notifyMsg, setNotifyMsg] = useState('')
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()

  useEffect(() => {
    const token = localStorage.getItem('token')

    if (token) {
      try {
        const decoded: DecodedToken = jwtDecode(token)
        const isExpired = decoded.exp * 1000 < Date.now()

        if (isExpired) {
          localStorage.removeItem('token')
        } else {
          navigate('/dashboard')
        }
      } catch (error) {
        localStorage.removeItem('token')
      }
    }
  }, [navigate])

  const handleLogin = async () => {
    setError('')
    try {
      const resp = await loginUser(email, password)
      if (resp.error) throw new Error(resp.error)
      localStorage.setItem('token', resp.access_token)
      const decoded: DecodedToken = jwtDecode(resp.access_token) as any
      i18n.changeLanguage(decoded.language_code || 'es')
      navigate('/dashboard')
    } catch (err: any) {
      let msg = t('login.errors.default')
      if (err.response?.data?.error) msg = err.response.data.error
      else if (err.message === 'login.errors.emailNotVerified')
        msg = t('login.errors.emailNotVerified')
      setError(msg)
    }
  }

  const handleRegisterSuccess = () => {
    setNotifyMsg(t('register.notifications.registerSuccess'))
    setNotify(true)
    setShowConfirm(true)
    setIsRegister(false)
  }

  const resetToLogin = () => {
    setShowConfirm(false)
    setIsRegister(false)
    setShowForgot(false)
  }

  return (
    <div className="relative w-full h-screen overflow-hidden">
      {/* Degradado + partículas de fondo */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-[#0A192F] to-[#060D1B]">
        <ParticlesComponent />
      </div>

      {/* Capa semitransparente para atenuar un poco */}
      <div className="absolute inset-0 bg-black/30" />

      {/* Formulario centrado */}
      <div className="relative z-10 flex items-center justify-center h-full px-4">
        <div className="bg-gray-900 bg-opacity-80 backdrop-blur-md rounded-xl p-6 sm:p-10 max-w-md w-full">
          {showConfirm ? (
            <ConfirmationEmailSent onGoLogin={resetToLogin} />
          ) : showForgot ? (
            <ForgotPasswordForm onBackToLogin={resetToLogin} />
          ) : isRegister ? (
            <RegisterForm
              toggleForm={() => setIsRegister(false)}
              onRegisterSuccess={handleRegisterSuccess}
            />
          ) : (
            <LoginForm
              email={email}
              password={password}
              setEmail={setEmail}
              setPassword={setPassword}
              handleSubmit={handleLogin}
              error={error}
              toggleForm={() => setIsRegister(true)}
              toggleForgotPassword={() => setShowForgot(true)}
            />
          )}
        </div>
      </div>

      {/* Modales y notificaciones */}
      {error && <ErrorModal message={error} onClose={() => setError('')} />}
      {notify && (
        <Notification message={notifyMsg} onClose={() => setNotify(false)} />
      )}
    </div>
  )
}

export default LoginPage
